import { StyledDatePicker, StyledPickersUnitWrapper, StyledPickersWrapper, StyledTimePicker } from "./styledComponents";
import { Typography } from "@kaltura/ds-react-components";
import dayjs from "dayjs";
import { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { createDateTime, DEFAULT_TIME_FORMAT } from "../utils";

interface PickersUnitProps {
    dateFieldName: string;
    timeFieldName: string;
    label: string;
    dateFormat: string;
    dateDefaultValue: string;
    timeDefaultValue: string;
    timeFormat?: string;

    /**
     * if true, inputs are disabled
     */
    disabled?: boolean;

    /**
     * if true, skips the 24-hour max duration validation
     * @default false
     */
    skipMaxDurationValidation?: boolean;

    "aria-describedby"?: string;
}

const PickersUnit = (props: PickersUnitProps) => {
    const {
        dateFieldName,
        timeFieldName,
        label,
        dateFormat,
        dateDefaultValue,
        timeDefaultValue,
        timeFormat = DEFAULT_TIME_FORMAT,
        disabled = false,
        skipMaxDurationValidation = false,
        "aria-describedby": ariaDescribedBy,
    } = props;
    const { control, watch, trigger } = useFormContext();
    // ref instead of prop because react-hook-form caches validateTimeRange at mount,
    // so the closure would read a stale value without this.
    const skipMaxDurationValidationRef = useRef(skipMaxDurationValidation);
    skipMaxDurationValidationRef.current = skipMaxDurationValidation;

    // Extract prefix from field names to construct all related field names
    // e.g., "publishing.schedule.startTimeDate" -> prefix = "publishing.schedule"
    // e.g., "publishing.startTimeDate" -> prefix = "publishing"
    // e.g., "startTimeDate" -> prefix = ""
    const parts = dateFieldName.split(".");
    const prefix = parts.length > 1 ? parts.slice(0, -1).join(".") : "";
    const startTimeDateField = prefix ? `${prefix}.startTimeDate` : "startTimeDate";
    const startTimeTimeField = prefix ? `${prefix}.startTimeTime` : "startTimeTime";
    const endTimeDateField = prefix ? `${prefix}.endTimeDate` : "endTimeDate";
    const endTimeTimeField = prefix ? `${prefix}.endTimeTime` : "endTimeTime";

    const validateTimeRange = () => {
        const startDate = watch(startTimeDateField);
        const endDate = watch(endTimeDateField);
        const startTime = watch(startTimeTimeField);
        const endTime = watch(endTimeTimeField);

        // Skip validation if any of the required values is missing
        if (!startDate || !endDate || !startTime || !endTime) {
            return true; // No validation error if we don't have all values yet
        }

        const startDateTime = createDateTime(startDate, startTime, dateFormat, DEFAULT_TIME_FORMAT);
        const endDateTime = createDateTime(endDate, endTime, dateFormat, DEFAULT_TIME_FORMAT);

        // Check if end datetime is before or equal to start datetime
        if (endDateTime.isBefore(startDateTime) || endDateTime.isSame(startDateTime)) {
            return translate("End time must be later than start time");
        }

        // Check if time difference is more than 24 hours
        if (!skipMaxDurationValidationRef.current) {
            const hoursDiff = endDateTime.diff(startDateTime, "hour");
            if (hoursDiff > 24) {
                return translate("Time gap between start and end time cannot be larger than 24 hours");
            }
        }

        return true;
    };

    const validateTimeRequiredWhenDateSet = (value: string) => {
        const date = watch(dateFieldName);

        if (date && !value) {
            return translate("Time must be set");
        }
        return true;
    };

    const handlePickerChange = (time: dayjs.Dayjs | null, onChange: (value: string) => void, isTime?: boolean) => {
        const formatToUse = isTime ? timeFormat : dateFormat;
        onChange(time ? time.format(formatToUse) : "");

        // Re-validate all time-related fields when any of them change
        trigger([startTimeDateField, startTimeTimeField, endTimeDateField, endTimeTimeField]);
    };

    return (
        <StyledPickersUnitWrapper>
            <Typography variant={"body2Highlight"}>{label}</Typography>
            <StyledPickersWrapper>
                <Controller
                    name={dateFieldName}
                    control={control}
                    defaultValue={dateDefaultValue}
                    rules={{
                        validate: {
                            timeRange: validateTimeRange,
                        },
                    }}
                    render={({ field: { value, onChange, ref, onBlur }, fieldState: { error } }) => (
                        <StyledDatePicker
                            inputRef={ref}
                            value={value ? dayjs(value, dateFormat) : null}
                            onChange={(date) => handlePickerChange(date, onChange)}
                            onBlur={onBlur}
                            format={dateFormat}
                            ariaLabel={translate("%1 date", [label])}
                            className={"chromatic-ignore"}
                            slotProps={{ textField: { fullWidth: true, error: !!error } }}
                            disabled={disabled}
                            ariaDescribedby={ariaDescribedBy}
                        />
                    )}
                />
                <Controller
                    name={timeFieldName}
                    control={control}
                    defaultValue={timeDefaultValue}
                    rules={{
                        validate: {
                            timeRange: validateTimeRange,
                            timeRequired: validateTimeRequiredWhenDateSet,
                        },
                    }}
                    render={({ field: { value, onChange, ref, onBlur }, fieldState: { error } }) => (
                        <StyledTimePicker
                            inputRef={ref}
                            value={value ? dayjs(value, timeFormat) : null}
                            onChange={(time) => handlePickerChange(time, onChange, true)}
                            onBlur={onBlur}
                            format={timeFormat}
                            ariaLabel={translate("%1 time", [label])}
                            ampm
                            className={"chromatic-ignore"}
                            fullWidth={true}
                            disabled={disabled}
                            ariaDescribedby={ariaDescribedBy}
                            slotProps={{
                                textField: {
                                    error: !!error,
                                },
                            }}
                        />
                    )}
                />
            </StyledPickersWrapper>
        </StyledPickersUnitWrapper>
    );
};

export default PickersUnit;
